/* eslint-disable max-lines-per-function, max-lines */
import "./product-details-favorite-button.scss";
import { api } from "jiffy-api";
import { EventBus, Events } from "jiffy-store";
import { sendDataLayer, track } from "jiffy-analytics";
import { on as onEvent } from "common/utils/ui/event";
import { until } from "common/utils/helpers/delay";

export default async () => {
  const button = document.querySelector(".js-product-details-favorite-button");
  const icon = document.querySelector(".js-product-details-favorite-icon");
  let $nextFocus = null;

  if (!button || !icon) return;

  // Get data from button or icon element
  const dataSource = button || icon;
  let { favoriteColor } = dataSource.dataset;
  const $microdata = document.getElementById("microdata");
  const { adwordsCategory } = $microdata ? $microdata.dataset : {};
  // eslint-disable-next-line no-unused-vars
  const {
    brand,
    productId,
    productStyle,
    productName,
    selectedCompositionId,
    compositionInventoryRank,
    shippingRule,
    businessLine,
    businessLineId,
    simplifiedPdp
  } = dataSource.dataset;
  const $$confirmationModal = $(".js-product-details-favorite-modal");
  const $$modalClose = $(".js-product-details-favorite-modal-close");
  const $$signInModal = $(".js-product-details-favorite-sign-in");
  const $$errorModal = $(".js-product-details-favorite-error-modal");
  const $errorModalAction = document.querySelector(
    ".js-product-details-favorite-error-modal-action"
  );
  const fetchFavoriteItemsIfNotFetched = async alreadyFetched => {
    // when currently not fetching the favorite items or has reached wait
    // timeout (not successfully awaited), fallback to fetching favorite
    // items
    if (alreadyFetched) return;

    try {
      window.jiffy.fetchingFavoriteItems = true;
      const data = await api.favorites.getFavoriteItems({ product_slug: productId });
      if (data.success) {
        window.jiffy.favoriteItems = data.items;
        window.jiffy.monitoredFavoriteItems = data.monitored_items;
        window.jiffy.fetchingFavoriteItems = false;
      }
    } catch {
      // silently ignore — non-critical UI; request layer reports actionable errors
    }
    window.jiffy.fetchingFavoriteItems = false;
    window.jiffy.favoriteItems = window.jiffy.favoriteItems || [];
    window.jiffy.monitoredFavoriteItems = window.jiffy.monitoredFavoriteItems || [];
  };

  const isCurrentContextFavorited = color =>
    window.jiffy.favoriteItems.some(
      item =>
        item.name === color &&
        item.business_line === (simplifiedPdp === "true" ? "Print" : businessLine)
    );

  window.jiffy = window.jiffy || {};

  // when favorite items have not been fetched yet
  if (!("favoriteItems" in window.jiffy)) {
    // if being fetched, wait until fetched or reached wait timeout
    let favoriteItemsFetched = false;
    if (window.jiffy.fetchingFavoriteItems) {
      favoriteItemsFetched = await until(() => "favoriteItems" in window.jiffy);
    }

    await fetchFavoriteItemsIfNotFetched(favoriteItemsFetched);
  }

  const clearButton = () => {
    button.classList.remove("product-details-favorite-button__button--added");
    icon.classList.remove("product-details-favorite-icon--added");
  };

  const fillButton = () => {
    button.classList.add("product-details-favorite-button__button--added");
    icon.classList.add("product-details-favorite-icon--added");
  };

  // Set initial button state based on current context
  if (isCurrentContextFavorited(favoriteColor)) {
    fillButton();
  } else {
    clearButton();
  }

  const trackData = {
    brand,
    product: productName,
    composition: favoriteColor,
    spree_composition_id: selectedCompositionId,
    composition_inventory_rank: compositionInventoryRank,
    fulfillment_group: shippingRule,
    product_category: adwordsCategory,
    version: 2,
    business_line: businessLine
  };

  const trackFavorited = (event, buttonDisplayType) => {
    track(event, {
      ...trackData,
      source: buttonDisplayType
    });
  };

  const trackModalActions = action => {
    track("favorite_modal_action", {
      ...trackData,
      action
    });
  };

  const trackModalActionsListeners = () => {
    const callback = ({ target: $target }) => {
      const { trackAction: action } = $target.dataset;
      trackModalActions(action);
    };

    onEvent("click", ".js-favorites-modal-track", callback, true);
  };

  trackModalActionsListeners();

  const addToFavorites = async buttonDisplayType => {
    try {
      const params = {
        product_slug: productId,
        ac: favoriteColor,
        business_line_id: businessLineId
      };
      await api.favorites.addFavoriteItem(params);

      $$confirmationModal.modal("show");
      $$confirmationModal.find("button").first().trigger("focus");
      fillButton();
      const targetBusinessLine = simplifiedPdp === "true" ? "Print" : businessLine;
      window.jiffy.favoriteItems = [
        ...window.jiffy.favoriteItems,
        { name: favoriteColor, business_line: targetBusinessLine }
      ];
      EventBus.publish(Events.ADDED_TO_FAVORITES, this, { color: favoriteColor });
      sendDataLayer({ event: "AddToWishlist" });
      trackFavorited("product_favorited", buttonDisplayType);
    } catch (e) {
      $errorModalAction.textContent = "Save Product To";
      $$errorModal.modal("show");
      track("product_favorite_failed", {
        ...trackData,
        source: buttonDisplayType,
        error: e.message || String(e)
      });

      throw new Error(`[JS] addToFavorites is failed by reason: ${e}`);
    } finally {
      button.disabled = false;
      icon.disabled = false;
    }
  };

  const removeFromFavorites = async buttonDisplayType => {
    try {
      await api.favorites.removeFavoriteItem(
        { ac: encodeURIComponent(favoriteColor) },
        {
          product_slug: productId,
          business_line_id: businessLineId
        }
      );

      clearButton();
      const targetBusinessLine = simplifiedPdp === "true" ? "Print" : businessLine;
      window.jiffy.favoriteItems = window.jiffy.favoriteItems.filter(
        item => !(item.name === favoriteColor && item.business_line === targetBusinessLine)
      );
      EventBus.publish(Events.REMOVED_FROM_FAVORITES, this, { color: favoriteColor });
      trackFavorited("product_unfavorited", buttonDisplayType);
    } catch (e) {
      $errorModalAction.textContent = "Remove Product From";
      $$errorModal.modal("show");
      track("product_unfavorite_failed", {
        ...trackData,
        source: buttonDisplayType,
        error: e.message || String(e)
      });

      throw new Error(`[JS] removeFromFavorites is failed by reason: ${e}`);
    } finally {
      button.disabled = false;
      icon.disabled = false;
      $nextFocus?.focus();
    }
  };

  const onButtonClick = buttonDisplayType => {
    button.disabled = true;
    icon.disabled = true;

    if (button.classList.contains("product-details-favorite-button__button--added")) {
      removeFromFavorites(buttonDisplayType);
      sendDataLayer({
        genericEvent: true,
        eventCategory: "Favorites",
        event: "RemoveFavorite",
        eventLabel: `removefavoritePDP:${productStyle}:${favoriteColor}`
      });
    } else {
      addToFavorites(buttonDisplayType);
      sendDataLayer({
        genericEvent: true,
        eventCategory: "Favorites",
        event: "AddFavorite",
        eventLabel: `favorite:${productStyle}:${favoriteColor}`
      });
    }
  };

  const onButtonWithSignInClick = async () => {
    $$signInModal.modal("show");
    $$signInModal.find("button").first().trigger("focus");

    const params = {
      product_slug: productId,
      ac: favoriteColor,
      business_line_id: businessLineId
    };
    await api.favorites.addFavoriteItem(params);
    sendDataLayer({
      genericEvent: true,
      eventCategory: "Favorites",
      event: "AddFavorite",
      eventLabel: `favorite:${productStyle}:${favoriteColor}`
    });
  };

  if ($$confirmationModal.length > 0) {
    button.addEventListener("click", () => {
      $nextFocus = button;
      onButtonClick("button");
    });
    icon.addEventListener("click", () => {
      $nextFocus = icon;
      onButtonClick("icon");
    });
  } else if ($$signInModal.length > 0) {
    button.addEventListener("click", () => {
      $nextFocus = button;
      onButtonWithSignInClick();
    });
    icon.addEventListener("click", () => {
      $nextFocus = icon;
      onButtonWithSignInClick();
    });
  }

  $$modalClose.on("click", () => {
    $nextFocus?.focus();
  });

  $$confirmationModal.on("hide.bs.modal", event => {
    event.stopPropagation();

    if ($$confirmationModal.is(":visible")) {
      trackModalActions("close_out_click");
    }
  });

  EventBus.on(Events.PRODUCT_COLOR_SELECTED, (evt, { color }) => {
    favoriteColor = color;

    if (isCurrentContextFavorited(favoriteColor)) {
      fillButton();
    } else {
      clearButton();
    }
  });
};
